import { ReactNode } from "react";
import { Box, MenuItem, Select, SelectProps, Typography } from "@mui/material";
import { FeArrowDownIcon } from "../../../components/cat15/FeIcons";

// Always drop the menu below the field and cap its height so long lists scroll.
const MENU_PROPS: SelectProps["MenuProps"] = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  PaperProps: { sx: { maxHeight: 320 } },
};

/** Figma select box: px 12, then text, 8px gap, 24px chevron. */
const PAD_X = 12;
const GAP = 8;
const ARROW = 24;
/** Value meaning "no filter". */
export const ALL = "all";

export interface OverviewSelectOption {
  value: string;
  label: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: OverviewSelectOption[];
  /** Shown (in the normal text colour) while `value` matches no option. */
  placeholder?: string;
  /** Leading 24px glyph, e.g. the calendar on the Reporting Year select. */
  startIcon?: ReactNode;
  /**
   * Extra right padding on the text, straight from Figma — it is what gives each
   * box its width in the default state (Sector 44, Borrower Type 12, Status 0,
   * Reporting Year 8).
   */
  textPr?: number;
  ariaLabel?: string;
}

/**
 * Borrowers-overview select (Figma Text_Box 24839:5248): white box, 1px #dedede
 * border, radius 8, 8/12 padding, Inter 400 16/24 text, grey vuesax chevron.
 */
const OverviewSelect = ({
  value,
  onChange,
  options,
  placeholder,
  startIcon,
  textPr = 0,
  ariaLabel,
}: Props) => (
  <Select
    size="small"
    value={value}
    displayEmpty
    onChange={(e) => onChange(e.target.value as string)}
    MenuProps={MENU_PROPS}
    inputProps={{ "aria-label": ariaLabel ?? placeholder }}
    IconComponent={(props) => (
      <FeArrowDownIcon
        {...props}
        sx={{
          fontSize: ARROW,
          color: "var(--gray1)",
          right: `${PAD_X}px`,
          top: `calc(50% - ${ARROW / 2}px)`,
        }}
      />
    )}
    renderValue={(v) => (
      <Box display="flex" alignItems="center" gap={`${GAP}px`}>
        {startIcon && (
          <Box
            display="flex"
            color="var(--gray1)"
            sx={{ "& svg": { fontSize: 24 } }}
          >
            {startIcon}
          </Box>
        )}
        <Typography
          fontSize="16px"
          fontWeight={400}
          lineHeight="24px"
          color="var(--dark)"
          pr={`${textPr}px`}
          noWrap
        >
          {/* "all" is the cleared state — Figma shows the field name, not "All". */}
          {v === ALL && placeholder
            ? placeholder
            : options.find((o) => o.value === v)?.label ?? placeholder}
        </Typography>
      </Box>
    )}
    sx={{
      bgcolor: "var(--white)",
      borderRadius: "8px",
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--gray2)" },
      "& .MuiSelect-select": {
        py: "8px",
        pl: `${PAD_X}px`,
        // text … gap … chevron … right padding
        pr: `${GAP + ARROW + PAD_X}px !important`,
        minHeight: "unset",
        display: "flex",
        alignItems: "center",
      },
    }}
  >
    {options.map((o) => (
      <MenuItem key={o.value} value={o.value}>
        {o.label}
      </MenuItem>
    ))}
  </Select>
);

export default OverviewSelect;
